import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { ChevronLeft, ChevronRight, Users, CalendarDays } from 'lucide-react';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS_DOT = { confirmed: 'bg-green-500', pending: 'bg-yellow-400', cancelled: 'bg-red-400' };
const STATUS_PILL = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700 line-through',
};

const pad = (n) => String(n).padStart(2, '0');
const keyOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

export const BookingCalendar = ({ bookings }) => {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [selected, setSelected] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);

  const byDate = useMemo(() => {
    const map = {};
    bookings
      .filter((b) => showCancelled || b.status !== 'cancelled')
      .forEach((b) => { (map[b.travel_date] ||= []).push(b); });
    return map;
  }, [bookings, showCancelled]);

  const first = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const leading = first.getDay();
  const cells = [...Array(leading).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthLabel = first.toLocaleString('en', { month: 'long', year: 'numeric' });
  const monthPrefix = `${cursor.y}-${pad(cursor.m + 1)}`;
  const monthBookings = Object.entries(byDate).filter(([k]) => k.startsWith(monthPrefix)).flatMap(([, v]) => v);
  const confirmedCount = monthBookings.filter((b) => b.status === 'confirmed').length;
  const travelers = monthBookings.filter((b) => b.status !== 'cancelled').reduce((s, b) => s + b.travelers, 0);

  const move = (delta) => {
    const d = new Date(cursor.y, cursor.m + delta, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
    setSelected(null);
  };

  const selectedList = selected ? byDate[selected] || [] : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="booking-calendar">
      <Card className="border-0 shadow-sm lg:col-span-2">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="calendar-prev" onClick={() => move(-1)} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Previous month"><ChevronLeft className="w-5 h-5" /></button>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900" data-testid="calendar-month-label">{monthLabel}</h3>
              <p className="text-xs text-gray-500" data-testid="calendar-month-summary">{confirmedCount} confirmed · {monthBookings.length} total · {travelers} travelers</p>
            </div>
            <button data-testid="calendar-next" onClick={() => move(1)} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Next month"><ChevronRight className="w-5 h-5" /></button>
          </div>

          <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-400 uppercase mb-2">
            {WEEKDAYS.map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} />;
              const k = keyOf(cursor.y, cursor.m, day);
              const list = byDate[k] || [];
              const isToday = k === keyOf(today.getFullYear(), today.getMonth(), today.getDate());
              const isSel = selected === k;
              return (
                <button
                  key={k}
                  data-testid={`calendar-day-${k}`}
                  onClick={() => setSelected(k)}
                  className={`relative h-20 rounded-lg border text-left p-2 transition-colors ${
                    isSel ? 'border-teal-500 bg-teal-50' : list.length ? 'border-gray-200 bg-white hover:bg-gray-50' : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <span className={`text-sm font-semibold ${isToday ? 'bg-teal-600 text-white rounded-full px-1.5 py-0.5' : 'text-gray-700'}`}>{day}</span>
                  {list.length > 0 && (
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="flex gap-1 mb-1">
                        {list.slice(0, 4).map((b) => <span key={b.booking_id} className={`w-2 h-2 rounded-full ${STATUS_DOT[b.status]}`} />)}
                      </div>
                      <p className="text-[11px] text-gray-600 truncate">{list.length === 1 ? list[0].destination : `${list.length} trips`}</p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Confirmed</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" />Pending</span>
            <label className="flex items-center gap-2 ml-auto cursor-pointer">
              <input data-testid="calendar-show-cancelled" type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} className="accent-teal-600" />
              Show cancelled
            </label>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-5" data-testid="calendar-day-details">
          {!selected ? (
            <div className="text-center text-gray-500 py-10">
              <CalendarDays className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              Select a day to see its trips
            </div>
          ) : (
            <>
              <h4 className="font-bold text-gray-900 mb-4">
                {new Date(`${selected}T00:00:00`).toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h4>
              {selectedList.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="calendar-day-empty">No trips departing this day.</p>
              ) : (
                <ul className="space-y-3">
                  {selectedList.map((b) => (
                    <li key={b.booking_id} data-testid={`calendar-booking-${b.booking_id}`} className="rounded-lg border border-gray-100 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-gray-900">{b.destination}</p>
                          {b.package_name && <p className="text-xs text-teal-700">{b.package_name}</p>}
                          <p className="text-sm text-gray-600 mt-1">{b.name} · {b.phone}</p>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_PILL[b.status]}`}>{b.status}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 flex items-center"><Users className="w-3 h-3 mr-1" />{b.travelers} traveler(s)</p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
